import Moment from 'moment';
import { withExpandedProductKeysInFilter } from '../../utility/filter.expansions';
import { trimAndAddAsterisk } from '../../utility/QueryUtility';
import { submissionsDateParamMappings } from './submissions.date-mapping';

export const SUBMISSIONS_QUERY_PARAM_MAPPING = [
  ...submissionsDateParamMappings,
  { path: 'page' },
  { path: 'size' },
  { path: 'dateFilter' },
  // Shared by every submissions search, so a caller that sends `teamIds` now has it forwarded. The callers that run with
  // the broker experience off send none, and `/submission/search` ignores it, so their requests are unchanged.
  { path: 'teamIds' },
  { path: 'hideReadOnly' },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: { orderBy: string; order: string }) => {
      const mappedOrderBy = SORT_KEY_MAPPING[orderBy] ?? orderBy;
      return mappedOrderBy && order ? `${mappedOrderBy},${order}` : null;
    },
  },
  {
    path: 'search',
    transform: ({ search = '' }) =>
      search ? trimAndAddAsterisk(search) : search,
  },
  {
    path: 'f',
    paramKey: 'filter',
    transform: ({ f }: { f: Record<string, any> }) => {
      // Every `filterType: 'date'` column in the submissions column config belongs here —
      // the Filter Modal emits `bf`/`af` as `YYYY-MM-DD`, which the API expects as ISO.
      const filterKeysToTransform = [
        'created',
        'modified',
        'expiryDate',
        'needBy',
      ];
      filterKeysToTransform.forEach((key) => {
        const filter = f?.[key];
        if (filter && typeof filter === 'object') {
          if (typeof filter.bf === 'string') {
            filter.bf = Moment.utc(filter.bf).toISOString();
          }
          if (typeof filter.af === 'string') {
            filter.af = Moment.utc(filter.af).toISOString();
          }
        }
      });
      return withExpandedProductKeysInFilter(f);
    },
  },
];

const SORT_KEY_MAPPING = {
  accountName: 'firmographics.name',
  agencyId: 'agencyAccount.agencyId',
  accountId: 'agencyAccount.accountId',
  createdDate: 'created',
  agent: 'agencyAccount.agent.firstName',
} as Record<string, string>;

/**
 * A search that names no team reads the caller's own teams. The server limits a team-realm agency caller to them, and for
 * any other caller `all` means no team filter, so Cowbell staff and agency admins see what they did before.
 */
export function withTeamScope<T extends Record<string, unknown>>(
  params: T
): T & { teamIds: unknown } {
  return { ...params, teamIds: params.teamIds ?? 'all' };
}
